import { useMemo, useState, useCallback, useEffect } from "react";
import {
    Modal,
    ModalHeader,
    ModalBody,
    TabContent,
    TabPane,
    Card,
    CardHeader,
    CardBody,
    Badge,
    Progress,
    Row,
    Col,
    Table,
    Button,
    ListGroup,
    ListGroupItem,
    Spinner,
    Toast,
    ToastBody,
} from "reactstrap";
import classnames from "classnames";
import { getDocumentsByUnitNo, exportMastersheetToExcel, requestLeasesZipExport } from "../TenantForm/Helpers/api";
import Swal from "sweetalert2";
import { DepositVsRentTab } from "./DepositVsRentTab";

export const DOCUMENT_TYPES = [
    { id: "lease_agreement", document_type: "Lease Agreement", title: "Lease Agreement", description: "Main lease contract documents", alwaysVisible: true },
    { id: "two_pieces_ids", document_type: "2 Pieces of IDS", title: "2 Pieces of IDS", description: "Two government issued ID documents", alwaysVisible: true },
    { id: "bank_statement", document_type: "Bank Statement", title: "Bank Statement", description: "Recent bank account statements", alwaysVisible: true },
    { id: "paystubs", document_type: "Paystubs", title: "Paystubs", description: "Recent salary payment stubs", alwaysVisible: true },
    { id: "pad_void_cheque", document_type: "PAD/Void Cheque", title: "PAD/Void Cheque", description: "Pre-authorized debit or void cheque", autoDelete: true, deleteDays: 45, alwaysVisible: true },
    { id: "insurance", document_type: "Insurance", title: "Insurance", description: "Tenant and property insurance documents", alwaysVisible: true },
    { id: "equifax", document_type: "Equifax", title: "Equifax", description: "Credit report from Equifax", alwaysVisible: true },
    { id: "freezing_pipe_notice", document_type: "Freezing Pipe Notice", title: "Freezing Pipe Notice", description: "Freezing pipe prevention notice", alwaysVisible: true },
    { id: "ug_parking_form", document_type: "UG Parking Form", title: "UG Parking Form", description: "Underground parking application form", alwaysVisible: true },
    { id: "storage_form", document_type: "Storage Form", title: "Storage Form", description: "Storage unit application form", alwaysVisible: true },
    { id: "amenity_form", document_type: "Amenity Form", title: "Amenity Form", description: "Building amenities access form", alwaysVisible: false },
    { id: "14_day_eviction_notice", document_type: "14 Day Eviction Notice", title: "14 Day Eviction Notice", description: "Official eviction notice document", alwaysVisible: false },
    { id: "rtdrs_form", document_type: "RTDRS Form", title: "RTDRS Form", description: "Residential Tenancy Dispute Resolution Service form", alwaysVisible: false },
    { id: "general_notice", document_type: "General Notice", title: "General Notice", description: "General building or tenant notices", alwaysVisible: false },
    { id: "incident_report", document_type: "Incident Report", title: "Incident Report", description: "Property or tenant incident reports", alwaysVisible: false },
    { id: "application_form", document_type: "Application Form", title: "Application Form", description: "Rental application form completed by the tenant", alwaysVisible: false },
    { id: "existing_tenant_documents", document_type: "Existing Tenant Documents", title: "Existing Tenant Documents", description: "Documents carried over from an existing tenancy", alwaysVisible: false },
];

const FIELD_LABELS = {
    unitNo: "Unit Number",
    tenant: "Tenant Name",
    style: "Unit Style",
    layout: "Layout Type",
    sqFt: "Square Footage",
    basicRent: "Basic Rent",
    targetRent: "Target Rent",
    ugFee: "Underground Parking Fee",
    extraTenantFee: "Extra Tenant Fee",
    dogFee: "Dog Fee",
    catFee: "Cat Fee",
    leaseStartDmy: "Lease Start Date",
    leaseEndDmy: "Lease End Date",
    insuranceExpiry: "Insurance Expiry Date",
    sd: "Security Deposit",
    status: "Unit Status",
    comments: "Comments",
    lease_status: "Lease Status",
    property_id: "Property ID",
    building_name: "Building Name",
};

const niceName = (key) => {
    if (FIELD_LABELS[key]) return FIELD_LABELS[key];

    return key
        .split("_")
        .map((w) => w[0].toUpperCase() + w.slice(1))
        .join(" ");
};

const getDocStatusFast = (unit, docType, docsCache) => {
    const cachedDocs = docsCache.get(unit.unitNo) || (unit.documents || []);
    const embedded = cachedDocs.filter(
        (d) => d.document_type === docType.document_type
    );
    const embeddedCount = embedded.reduce(
        (sum, d) => sum + 1 + (d.previous_versions?.length || 0),
        0
    );

    if (embeddedCount > 0) {
        return { status: "Uploaded", count: embeddedCount };
    }

    const na = unit.documentsNA && unit.documentsNA[docType.id] === true;
    if (na) {
        return { status: "N/A", count: 0 };
    }

    return { status: "Missing", count: 0 };
};

const getDocStatusFromAPI = (apiDocs, docType, naFlag) => {
    const uploaded = apiDocs.filter((d) => d.document_type === docType.document_type);
    const count = uploaded.reduce((s, d) => s + 1 + (d.previous_versions?.length || 0), 0);

    if (count > 0) return { status: "Uploaded", count, docs: uploaded };
    if (naFlag) return { status: "N/A", count: 0 };
    return { status: "Missing", count: 0 };
};

const isUnitInvalid = (unit, docsCache) => {
    const insuranceExpired = unit.insuranceExpiry && new Date(unit.insuranceExpiry) < new Date();
    const required = DOCUMENT_TYPES.filter(d => d.alwaysVisible);
    const hasMissing = required.some(doc => getDocStatusFast(unit, doc, docsCache).status === "Missing");
    return insuranceExpired || hasMissing;
};

const getTenantName = (unit) => {
    if (unit.tenant && String(unit.tenant).trim()) {
        return String(unit.tenant).trim();
    }
    if (unit.tenants && String(unit.tenants).trim()) {
        return String(unit.tenants).trim();
    }
    if (unit.lease_status?.has_active_lease && unit.lease_status.tenants?.length > 0) {
        return unit.lease_status.tenants
            .map((t) => t.name || (t.email ? t.email.split('@')[0] : ''))
            .filter(Boolean)
            .join(" & ") || "Tenant";
    }
    return "—";
};

const getSqFt = (unit) => {
    return unit.sqFt || unit.totalAreaSqft || unit.sqft || "—";
};

const ReportModal = ({ isOpen, toggle, masterSheetData }) => {
    const [activeTab, setActiveTab] = useState("summary");
    const [detailUnit, setDetailUnit] = useState(null);
    const [detailDocs, setDetailDocs] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState(null);
    const [docsCache, setDocsCache] = useState(new Map());
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [excelLoading, setExcelLoading] = useState(false);
    const [excelToast, setExcelToast] = useState({ show: false, success: false, message: "" });
    const [zipExportLoading, setZipExportLoading] = useState(false);

    const fetchAllUnitDocs = useCallback(async () => {
        if (!masterSheetData?.items?.length) return;
        if (docsCache.size >= masterSheetData.items.length) return;

        setSummaryLoading(true);
        try {
            const promises = masterSheetData.items.map((unit) =>
                getDocumentsByUnitNo(
                    masterSheetData.property_id,
                    masterSheetData.building_name,
                    unit.unitNo
                )
                    .then((resp) => ({
                        unitNo: unit.unitNo,
                        docs: resp?.success ? (resp.data?.documents || []) : [],
                    }))
                    .catch((err) => {
                        console.error(`Failed to fetch docs for ${unit.unitNo}:`, err);
                        return { unitNo: unit.unitNo, docs: [] };
                    })
            );

            const results = await Promise.all(promises);
            const newCache = new Map(docsCache);
            results.forEach(({ unitNo, docs }) => newCache.set(unitNo, docs));
            setDocsCache(newCache);
        } catch (err) {
            console.error("Failed to fetch all unit documents:", err);
        } finally {
            setSummaryLoading(false);
        }
    }, [masterSheetData, docsCache]);

    useEffect(() => {
        if (isOpen && masterSheetData?.items?.length) {
            fetchAllUnitDocs();
        } else if (!isOpen) {
            setDocsCache(new Map());
            setDetailUnit(null);
            setDetailDocs(null);
        }
    }, [isOpen, masterSheetData, fetchAllUnitDocs]);

    /* ------------------- FETCH SINGLE UNIT ------------------- */
    const fetchUnitDocs = useCallback(
        async (unit) => {
            const cacheKey = unit.unitNo;
            if (docsCache.has(cacheKey)) {
                setDetailDocs(docsCache.get(cacheKey));
                return;
            }
            setDetailLoading(true);
            setDetailError(null);
            try {
                const resp = await getDocumentsByUnitNo(
                    masterSheetData.property_id,
                    masterSheetData.building_name,
                    unit.unitNo
                );
                if (!resp?.success) throw new Error(resp?.message || "Failed");
                const docs = resp.data.documents || [];
                const newCache = new Map(docsCache);
                newCache.set(cacheKey, docs);
                setDocsCache(newCache);
                setDetailDocs(docs);
            } catch (err) {
                console.error(err);
                setDetailError("Could not load documents for this unit.");
            } finally {
                setDetailLoading(false);
            }
        },
        [masterSheetData, docsCache]
    );

    const openDetail = (unit) => {
        setDetailUnit(unit);
        fetchUnitDocs(unit);
    };

    /* ------------------- SUMMARY & TABLE LOGIC ------------------- */
    const summary = useMemo(() => {
        if (!masterSheetData?.items?.length) return null;

        const total = masterSheetData.items.length;
        const withTenant = masterSheetData.items.filter((i) => i.tenant);
        const withoutInsurance = masterSheetData.items.filter((i) => !i.insuranceExpiry);
        const expiredInsurance = masterSheetData.items
            .filter((i) => i.insuranceExpiry && new Date(i.insuranceExpiry) < new Date())
            .map((i) => ({ unitNo: i.unitNo, expiry: new Date(i.insuranceExpiry) }));

        const required = DOCUMENT_TYPES.filter((d) => d.alwaysVisible);

        const withAllRequired = masterSheetData.items.filter((unit) => {
            const anyUploaded = DOCUMENT_TYPES.some((doc) => {
                const { status } = getDocStatusFast(unit, doc, docsCache);
                return status === "Uploaded";
            });
            return anyUploaded;
        });

        const missingRequired = masterSheetData.items
            .map((u) => ({
                unitNo: u.unitNo,
                missing: required.filter((doc) => getDocStatusFast(u, doc, docsCache).status === "Missing"),
            }))
            .filter((x) => x.missing.length);

        return {
            total,
            withTenant: withTenant.length,
            withoutInsurance: withoutInsurance.length,
            expiredInsurance,
            withAllRequired: withAllRequired.length,
            missingRequired,
        };
    }, [masterSheetData, docsCache]);

    const progress = (v, t) => (t ? Math.round((v / t) * 100) : 0);

    const sortedUnits = useMemo(() => {
        if (!masterSheetData?.items?.length) return [];

        return [...masterSheetData.items].sort((a, b) => {
            const aNum = parseFloat(String(a.unitNo));
            const bNum = parseFloat(String(b.unitNo));
            if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
            return String(a.unitNo).localeCompare(String(b.unitNo));
        });
    }, [masterSheetData]);

    const tableRows = useMemo(() => {
        if (!sortedUnits.length) return [];

        const required = DOCUMENT_TYPES.filter(d => d.alwaysVisible);

        return sortedUnits.map((unit) => {
            const requiredMissing = required.filter(doc =>
                getDocStatusFast(unit, doc, docsCache).status === "Missing"
            ).length;

            const insuranceExpired = unit.insuranceExpiry && new Date(unit.insuranceExpiry) < new Date();
            const totalDocCount = DOCUMENT_TYPES.reduce((sum, doc) => {
                const { count } = getDocStatusFast(unit, doc, docsCache);
                return sum + count;
            }, 0);

            const rowIsInvalid = isUnitInvalid(unit, docsCache);

            const leaseStart = unit.leaseStartDmy ? new Date(unit.leaseStartDmy).toLocaleDateString("en-GB") : null;
            const leaseEnd = unit.leaseEndDmy ? new Date(unit.leaseEndDmy).toLocaleDateString("en-GB") : null;
            const leaseStr = leaseStart && leaseEnd ? `${leaseStart} to ${leaseEnd}` : "No Active Lease";

            const insuranceStr = unit.insuranceExpiry
                ? new Date(unit.insuranceExpiry).toLocaleDateString("en-GB") + (insuranceExpired ? " EXPIRED" : "")
                : "—";

            return {
                unit,
                tenantName: getTenantName(unit),
                sqFt: getSqFt(unit),
                rent: unit.basicRent != null ? `$${Number(unit.basicRent).toFixed(2)}` : "—",
                lease: leaseStr,
                insurance: insuranceStr,
                requiredMissing,
                totalDocCount,
                rowIsInvalid,
                insuranceExpired,
            };
        });
    }, [sortedUnits, docsCache]);

    const handleLeasesZipExport = async () => {
        if (!masterSheetData?._id) {
            Swal.fire({ icon: "error", title: "Cannot start export", text: "MasterSheet ID not found." });
            return;
        }

        const confirm = await Swal.fire({
            title: "Email Lease Bundle?",
            html: `
              <div style="text-align: left; font-size: 14px;">
                <p>We'll collect the <strong>latest active lease</strong> for every unit in <strong>${masterSheetData.building_name || 'this mastersheet'}</strong>:</p>
                <ul style="padding-left: 18px; margin: 8px 0;">
                  <li>If a lease was signed on RentDigiCare, the same PDF you see in <em>Preview Lease</em> is used.</li>
                  <li>Otherwise the uploaded <em>Lease Agreement</em> document is used.</li>
                </ul>
                <p style="margin-top: 10px;">The bundle will be emailed to you as a ZIP when ready. You don't need to keep this tab open.</p>
              </div>
            `,
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Start & Email Me",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#183B4E",
            cancelButtonColor: "#6c757d",
            reverseButtons: true,
        });

        if (!confirm.isConfirmed) return;

        setZipExportLoading(true);
        try {
            const resp = await requestLeasesZipExport(masterSheetData._id);
            await Swal.fire({
                icon: "success",
                title: "We're on it",
                html: `
                  <div style="text-align: left; font-size: 14px;">
                    <p>Your lease bundle is being prepared in the background.</p>
                    <p style="margin-top: 8px;">When it's done, the ZIP will be emailed to <strong>${resp?.deliveryEmail || 'you'}</strong>.</p>
                    <p style="margin-top: 8px; color: #666; font-size: 12px;">You can close this dialog and keep working — generation may take a few minutes for larger buildings.</p>
                  </div>
                `,
                confirmButtonText: "Got it",
                confirmButtonColor: "#183B4E",
            });
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || "Failed to start the export.";
            Swal.fire({ icon: "error", title: "Couldn't start export", text: msg });
            console.error("Leases ZIP export error:", err);
        } finally {
            setZipExportLoading(false);
        }
    };

    const handleExcelExport = async () => {
        if (!masterSheetData?._id) {
            setExcelToast({ show: true, success: false, message: "MasterSheet ID not found." });
            return;
        }

        const result = await Swal.fire({
            title: "Download Excel Report?",
            text: `This will export the full MasterSheet for "${masterSheetData.building_name}" as an Excel file.`,
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Yes, Download",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#28a745",
            cancelButtonColor: "#6c757d",
            reverseButtons: true,
        });

        if (!result.isConfirmed) return;

        setExcelLoading(true);
        try {
            await exportMastersheetToExcel(masterSheetData._id, {});
            setExcelToast({ show: true, success: true, message: "Excel report downloaded successfully!" });
        } catch (err) {
            setExcelToast({ show: true, success: false, message: "Failed to download Excel report." });
            console.error("Excel export error:", err);
        } finally {
            setExcelLoading(false);
            setTimeout(() => setExcelToast(prev => ({ ...prev, show: false })), 4000);
        }
    };

    return (
        <>
            {/* Toast */}
            <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1100 }}>
                <Toast isOpen={excelToast.show} fade>
                    <ToastBody>{excelToast.message}</ToastBody>
                </Toast>
            </div>

            {/* Main Modal */}
            <Modal isOpen={isOpen} toggle={toggle} size="xl" scrollable>
                <ModalHeader toggle={toggle}>
                    <div className="d-flex justify-content-between align-items-center w-100">
                        <span>
                            Master-Sheet Report – {masterSheetData?.building_name || "N/A"}
                        </span>
                    </div>
                </ModalHeader>

                <ModalBody className="p-0">
                    <div className="d-flex mt-3 mb-3 ms-3 align-items-center">
                        <div className="d-flex">
                            <button
                                className={`btn ${activeTab === "summary" ? "btn-primary" : "btn-outline-primary"} me-2`}
                                onClick={() => setActiveTab("summary")}
                            >
                                Summary
                            </button>
                            <button
                                className={`btn ${activeTab === "detail" ? "btn-primary" : "btn-outline-primary"}`}
                                onClick={() => setActiveTab("detail")}
                            >
                                Detailed
                            </button>
                            <button
                                className={`btn ms-2 ${activeTab === "depositVsRent" ? "btn-primary" : "btn-outline-primary"}`}
                                onClick={() => setActiveTab("depositVsRent")}
                            >
                                SD vs Rent
                            </button>
                        </div>
                        <button
                            className={`d-flex align-items-center gap-1 ms-auto me-2 btn ${activeTab === "detail" ? "btn-dark" : "btn-outline-dark"}`}
                            onClick={handleLeasesZipExport}
                            disabled={zipExportLoading || !masterSheetData?._id}
                            title="Email me a ZIP of every unit's latest active lease"
                        >
                            {zipExportLoading ? <Spinner size="sm" /> : "Email Leases ZIP"}
                        </button>
                        <button
                            className={`d-flex align-items-center gap-1 me-3 btn ${activeTab === "detail" ? "btn-success" : "btn-outline-success"}`}
                            onClick={handleExcelExport}
                            disabled={excelLoading || !masterSheetData?._id}
                        >
                            {excelLoading ? <Spinner size="sm" /> : "Download Excel"}
                        </button>
                    </div>

                    <TabContent activeTab={activeTab} className="p-4">
                        {/* Summary Tab - unchanged */}
                        <TabPane tabId="summary">
                            {summaryLoading ? (
                                <div className="text-center p-4">
                                    <Spinner color="primary" /> Loading document status...
                                </div>
                            ) : !summary ? (
                                <p className="text-muted">No data available.</p>
                            ) : (
                                <Row>
                                    <Col md={12}>
                                        <Card className="mb-3">
                                            <CardHeader className="bg-primary text-white">
                                                Units Overview (Total Units: {summary.total})
                                            </CardHeader>
                                            <CardBody>
                                                <p style={{ marginBottom: '0.25rem' }}>
                                                    <strong>With Tenant:</strong> {summary.withTenant} ({progress(summary.withTenant, summary.total)}%)
                                                </p>
                                                <Progress value={progress(summary.withTenant, summary.total)} color="info" className="mb-3" />

                                                <p style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                                                    <span>
                                                        <strong>Without Insurance Date:</strong> {summary.withoutInsurance} ({progress(summary.withoutInsurance, summary.total)}%)
                                                    </span>
                                                    <span style={{ color: 'red', fontSize: '0.85em' }}>
                                                        {summary.expiredInsurance.length} expired
                                                    </span>
                                                </p>
                                                <Progress value={progress(summary.withoutInsurance, summary.total)} color="warning" className="mb-3" />

                                                <p style={{ marginBottom: '0.25rem' }}>
                                                    <strong>All Required Docs:</strong> {summary.withAllRequired} ({progress(summary.withAllRequired, summary.total)}%)
                                                </p>
                                                <Progress value={progress(summary.withAllRequired, summary.total)} color="success" />
                                            </CardBody>
                                        </Card>
                                    </Col>
                                </Row>
                            )}
                        </TabPane>

                        {/* Detailed Table */}
                        <TabPane tabId="detail">
                            {masterSheetData?.items?.length ? (
                                <Table hover responsive className="table-sm">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Unit #</th>
                                            <th>Tenant</th>
                                            <th>Style / Layout</th>
                                            <th>SqFt</th>
                                            <th>Rent</th>
                                            <th>Lease</th>
                                            <th>Insurance</th>
                                            <th className="text-center">Docs</th>
                                            <th className="text-center">Missing</th>
                                            <th className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tableRows.map(({ unit, requiredMissing, insuranceExpired, totalDocCount, rowIsInvalid }) => (
                                            <tr
                                                key={unit._id || unit.unitNo}
                                                className={classnames({
                                                    "table-danger": rowIsInvalid,
                                                    "table-success": !rowIsInvalid
                                                })}
                                                style={{ cursor: "pointer" }}
                                                onClick={() => openDetail(unit)}
                                            >
                                                <td><strong>{unit.unitNo}</strong></td>
                                                <td>{unit.tenant || unit.tenants || "—"}</td>
                                                <td>{unit.style || ""} / {unit.layout || ""}</td>
                                                <td>{getSqFt(unit) || "—"}</td>
                                                <td>${unit.basicRent ?? "—"}</td>
                                                <td>
                                                    {unit.leaseStartDmy
                                                        ? new Date(unit.leaseStartDmy).toLocaleDateString("en-GB")
                                                        : "—"}
                                                    {" to "}
                                                    {unit.leaseEndDmy
                                                        ? new Date(unit.leaseEndDmy).toLocaleDateString("en-GB")
                                                        : "—"}
                                                </td>
                                                <td>
                                                    {unit.insuranceExpiry
                                                        ? new Date(unit.insuranceExpiry).toLocaleDateString("en-GB")
                                                        : "—"}
                                                    {insuranceExpired && <Badge color="danger" className="bg-danger ms-2">EXPIRED</Badge>}
                                                </td>
                                                <td className="text-center">
                                                    <Badge color="info" className="text-black">{totalDocCount}</Badge>
                                                </td>
                                                <td className="text-center">
                                                    {requiredMissing.length ? (
                                                        <Badge color="warning" className="text-black">{requiredMissing.length}</Badge>
                                                    ) : (
                                                        <Badge color="success" className="text-black">All</Badge>
                                                    )}
                                                </td>
                                                <td className="text-center">
                                                    <Button size="sm" color="primary" outline>
                                                        View Details
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            ) : (
                                <p className="text-muted text-center">No units to display.</p>
                            )}
                        </TabPane>
                        <TabPane tabId="depositVsRent">
                            <DepositVsRentTab masterSheetData={masterSheetData} />
                        </TabPane>
                    </TabContent>
                </ModalBody>
            </Modal>

            {/* Detail Modal - tenant name now uses smart resolver */}
            <Modal isOpen={!!detailUnit} toggle={() => setDetailUnit(null)} size="lg" scrollable>
                <ModalHeader toggle={() => setDetailUnit(null)}>
                    Unit {detailUnit?.unitNo} – {getTenantName(detailUnit || {})}
                </ModalHeader>
                <ModalBody>
                    {detailUnit && (
                        <>
                            <Card className="mb-3">
                                <CardHeader className="bg-info text-white">Basic Information</CardHeader>
                                <CardBody>
                                    <Row>
                                        {Object.entries(detailUnit)
                                            .filter(
                                                ([k]) =>
                                                    ![
                                                        "_id",
                                                        "createdAt",
                                                        "updatedAt",
                                                        "documentsNA",
                                                        "documents",
                                                        "meta",
                                                        "lease_status",
                                                        "style_label",
                                                        "layout_label",
                                                        "sqFt_label",
                                                        "unitNo_label",
                                                        "tenant_label",
                                                        "sd_label",
                                                        "targetRent_label",
                                                        "basicRent_label",
                                                        "ugFee_label",
                                                        "extraTenantFee_label",
                                                        "dogFee_label",
                                                        "catFee_label",
                                                        "leaseStartDmy_label",
                                                        "leaseEndDmy_label",
                                                        "insuranceExpiry_label",
                                                        "status_label",
                                                        "comments_label",
                                                    ].includes(k)
                                            )
                                            .map(([key, val]) => (
                                                <Col md={6} key={key} className="mb-2">
                                                    <strong>{niceName(key)}:</strong>{" "}
                                                    {key.includes("Dmy") || key === "insuranceExpiry"
                                                        ? val
                                                            ? new Date(val).toLocaleDateString("en-GB")
                                                            : "—"
                                                        : val ?? "—"}
                                                </Col>
                                            ))}
                                    </Row>
                                </CardBody>
                            </Card>

                            {/* DOCUMENTS */}
                            <Card className="mb-3">
                                <CardHeader className="bg-secondary text-white">
                                    Documents {detailLoading && <Spinner size="sm" className="ms-2" />}
                                </CardHeader>
                                <CardBody>
                                    {detailLoading ? (
                                        <p className="text-center text-muted">Loading documents…</p>
                                    ) : detailError ? (
                                        <p className="text-danger">{detailError}</p>
                                    ) : (
                                        DOCUMENT_TYPES.map((doc) => {
                                            const na = detailUnit.documentsNA && detailUnit.documentsNA[doc.id] === true;
                                            const { status, count } = getDocStatusFromAPI(detailDocs || [], doc, na);

                                            let badgeColor = "secondary";
                                            if (status === "Uploaded") badgeColor = "success";
                                            if (status === "Missing") badgeColor = "danger";

                                            return (
                                                <div
                                                    key={doc.id}
                                                    className="d-flex justify-content-between align-items-center py-2 border-bottom"
                                                >
                                                    <div>
                                                        <strong>{doc.title}</strong>
                                                        <small className="d-block text-muted">{doc.description}</small>
                                                    </div>
                                                    <div className="d-flex align-items-center gap-2">
                                                        {count > 0 && (
                                                            <Badge color="info" pill>
                                                                <span className="text-secondary">{count}</span>
                                                            </Badge>
                                                        )}
                                                        <Badge color={badgeColor}>
                                                            <span className="text-secondary">{status}</span>
                                                        </Badge>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </CardBody>
                            </Card>

                            {/* META */}
                            {detailUnit.meta && Object.keys(detailUnit.meta).length > 0 && (
                                <Card>
                                    <CardHeader className="bg-dark text-white">Meta Information</CardHeader>
                                    <CardBody>
                                        {Object.entries(detailUnit.meta).map(([section, data]) => (
                                            <div key={section} className="mb-3">
                                                <h6 className="text-capitalize">{niceName(section)}</h6>
                                                {Array.isArray(data) ? (
                                                    data.length ? (
                                                        <Table size="sm" bordered>
                                                            <thead>
                                                                <tr>
                                                                    {Object.keys(data[0]).map((h) => (
                                                                        <th key={h}>{niceName(h)}</th>
                                                                    ))}
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {data.map((row, i) => (
                                                                    <tr key={i}>
                                                                        {Object.values(row).map((v, j) => (
                                                                            <td key={j}>{v ?? "—"}</td>
                                                                        ))}
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </Table>
                                                    ) : (
                                                        <p className="text-muted">—</p>
                                                    )
                                                ) : typeof data === "object" ? (
                                                    <ListGroup flush>
                                                        {Object.entries(data).map(([k, v]) => (
                                                            <ListGroupItem key={k}>
                                                                <strong>{niceName(k)}:</strong> {v ?? "—"}
                                                            </ListGroupItem>
                                                        ))}
                                                    </ListGroup>
                                                ) : (
                                                    <p>{data ?? "—"}</p>
                                                )}
                                            </div>
                                        ))}
                                    </CardBody>
                                </Card>
                            )}
                        </>
                    )}
                </ModalBody>
            </Modal>
        </>
    );
};

export { ReportModal };